import type {NavigateFunction} from 'react-router-dom'
import {getNationalExamDocId, type NationalExamItem} from '../components/national-exams/types'
import {
  getNationalExamFromNotification,
  type NationalExamNotificationRef,
} from './notification-national-exam'

export const NATIONAL_EXAM_NOTIFICATION_TYPES = {
  ADD: 'ADD_NATIONAL_EXAM',
  CORRECTION_ADDED: 'NATIONAL_EXAM_CORRECTION_ADDED',
} as const

export type NationalExamNotificationDeepLink = {
  examId: string
  openCorrection: boolean
  tab: 'exam' | 'video' | 'qcm'
}

/** Mongo id from `nationalExam._id`, `nationalExamId`, or push `data.nationalExamId`. */
export function getNationalExamNotificationExamId(notification: {
  nationalExam?: unknown
  nationalExamId?: string
  data?: {nationalExamId?: string}
}): string | undefined {
  const ne = getNationalExamFromNotification(notification)
  if (ne) {
    const fromNe =
      (typeof ne._id === 'string' && ne._id.trim()) ||
      (typeof ne.id === 'string' && ne.id.trim()) ||
      ''
    if (fromNe) {
      return fromNe
    }
  }
  const top = notification as {nationalExamId?: string}
  if (typeof top.nationalExamId === 'string' && top.nationalExamId.trim()) {
    return top.nationalExamId.trim()
  }
  const dataId = notification.data?.nationalExamId
  if (typeof dataId === 'string' && dataId.trim()) {
    return dataId.trim()
  }
  return undefined
}

export function nationalExamNotificationTab(
  ne: NationalExamNotificationRef | null
): 'exam' | 'video' | 'qcm' {
  const raw = (ne?.type ?? '').trim().toLowerCase()
  if (raw === 'video') {
    return 'video'
  }
  if (raw === 'qcm') {
    return 'qcm'
  }
  return 'exam'
}

export function parseNationalExamNotificationDeepLink(
  notification: {notificationType?: string; nationalExam?: unknown; nationalExamId?: string; data?: {nationalExamId?: string}}
): NationalExamNotificationDeepLink | null {
  const type = notification.notificationType
  if (
    type !== NATIONAL_EXAM_NOTIFICATION_TYPES.ADD &&
    type !== NATIONAL_EXAM_NOTIFICATION_TYPES.CORRECTION_ADDED
  ) {
    return null
  }
  const examId = getNationalExamNotificationExamId(notification)
  if (!examId) {
    return null
  }
  const ne = getNationalExamFromNotification(notification)
  return {
    examId,
    openCorrection: type === NATIONAL_EXAM_NOTIFICATION_TYPES.CORRECTION_ADDED,
    tab: nationalExamNotificationTab(ne),
  }
}

/** Web route: `/national-exams?examId=…&openCorrection=1&tab=exam` */
export function buildNationalExamsNotificationPath(notification: {
  notificationType?: string
  url?: string
  nationalExam?: unknown
  nationalExamId?: string
  data?: {nationalExamId?: string}
}): string {
  const link = parseNationalExamNotificationDeepLink(notification)
  const base = (notification.url?.trim() || '/national-exams').split('?')[0]
  if (!link) {
    return base
  }
  const params = new URLSearchParams()
  params.set('examId', link.examId)
  if (link.tab !== 'exam') {
    params.set('tab', link.tab)
  }
  if (link.openCorrection) {
    params.set('openCorrection', '1')
  }
  return `${base}?${params.toString()}`
}

export function isNationalExamNotificationType(type: string | undefined): boolean {
  return (
    type === NATIONAL_EXAM_NOTIFICATION_TYPES.ADD ||
    type === NATIONAL_EXAM_NOTIFICATION_TYPES.CORRECTION_ADDED
  )
}

export function nationalExamNotificationRefToItem(
  ne: NationalExamNotificationRef | null
): NationalExamItem | null {
  if (!ne) {
    return null
  }
  const id =
    (typeof ne._id === 'string' && ne._id.trim()) ||
    (typeof ne.id === 'string' && ne.id.trim()) ||
    undefined
  return {
    ...(id ? {_id: id, id} : {}),
    title: ne.title,
    subject: ne.subject,
    level: ne.level === '9eme' || ne.level === '6eme' ? ne.level : undefined,
    year: typeof ne.year === 'number' ? ne.year : undefined,
    session: ne.session,
    type: ne.type === 'video' || ne.type === 'qcm' || ne.type === 'exam' ? ne.type : undefined,
  }
}

export function findNationalExamInLists(
  examId: string,
  lists: NationalExamItem[][]
): NationalExamItem | undefined {
  const target = examId.trim()
  if (!target) {
    return undefined
  }
  for (const list of lists) {
    const hit = list.find((item) => getNationalExamDocId(item) === target)
    if (hit) {
      return hit
    }
  }
  return undefined
}

export function navigateNationalExamNotification(
  navigate: NavigateFunction,
  notification: Parameters<typeof buildNationalExamsNotificationPath>[0]
): void {
  navigate(buildNationalExamsNotificationPath(notification))
}
